import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Icon } from './Icon';
import { Text } from './Text';
import { radius, toneColors, useTheme, type Tone } from './theme';

type ToastItem = { id: number; message: string; tone: Tone };
interface ToastApi {
  show: (message: string, tone?: Tone) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}
const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (message: string, tone: Tone = 'neutral') => {
      const id = ++seq.current;
      setItems((l) => [...l.slice(-3), { id, message, tone }]);
      setTimeout(() => dismiss(id), tone === 'danger' ? 6000 : 3500);
    },
    [dismiss],
  );
  const api = useMemo<ToastApi>(() => ({ show, success: (m) => show(m, 'success'), error: (m) => show(m, 'danger'), info: (m) => show(m, 'info') }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <View style={{ pointerEvents: 'box-none', position: 'absolute', left: 0, right: 0, bottom: insets.bottom + 72, alignItems: 'center', gap: 8, paddingHorizontal: 16 }}>
        {items.map((t) => {
          const c = toneColors(colors, t.tone);
          return (
            <Pressable key={t.id} onPress={() => dismiss(t.id)} accessibilityRole="alert" accessibilityLiveRegion="polite" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, maxWidth: 480, width: '100%', paddingHorizontal: 14, paddingVertical: 12, borderRadius: radius.lg, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, borderLeftWidth: 4, borderLeftColor: c.fg, shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 4 }, elevation: 4 }}>
              <Icon name={t.tone === 'success' ? 'checkCircle' : t.tone === 'danger' ? 'alert' : 'info'} size={16} color={c.fg} />
              <Text style={{ flex: 1 }}>{t.message}</Text>
            </Pressable>
          );
        })}
      </View>
    </Ctx.Provider>
  );
}

export function useToast() {
  const c = useContext(Ctx);
  if (!c) throw new Error('ToastProvider missing');
  return c;
}
